"use client";

import {
  AlertCircle,
  BookA,
  BookMarked,
  BookOpen,
  BookOpenCheck,
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  Code,
  Database,
  FileText,
  Film,
  Globe,
  GraduationCap,
  Image,
  Info,
  type LucideIcon,
  MessageCircle,
  Mic,
  Newspaper,
  Podcast,
  Sparkles,
  Users,
  Video,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useToolStrings } from "@/components/citation-tool/tool-strings";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  analyzeMissingFields,
  detectSourceType,
  type FieldConfig,
  getSourceTypeById,
  SOURCE_TYPES,
  type SourceType,
  type WizardEntry,
} from "@/lib/source-types";
import { cn } from "@/lib/utils";

const ICON_MAP: Record<string, LucideIcon> = {
  Globe,
  BookA,
  BookOpen,
  FileText,
  Newspaper,
  Video,
  Podcast,
  Film,
  MessageCircle,
  BookOpenCheck,
  Database,
  Code,
  Mic,
  ClipboardList,
  GraduationCap,
  BookMarked,
  Users,
  Image,
};

interface CitationWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: WizardEntry;
  citation: string;
  style: string;
  onComplete: (updatedEntry: WizardEntry) => void;
}

export function CitationWizard({
  open,
  onOpenChange,
  entry,
  citation,
  style,
  onComplete,
}: CitationWizardProps) {
  const strings = useToolStrings();
  // Detect source type from entry or default to website
  const initialSourceType = detectSourceType(entry.type);
  const [selectedType, setSelectedType] =
    useState<SourceType>(initialSourceType);
  const [formData, setFormData] = useState<WizardEntry>({ ...entry });
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset form when entry changes
  useEffect(() => {
    setFormData({ ...entry });
    setSelectedType(detectSourceType(entry.type));
  }, [entry]);

  const fieldAnalysis = analyzeMissingFields(formData, selectedType);
  const missingRequired = fieldAnalysis.filter(
    (a) => a.status === "missing" && a.field.requirement === "required"
  );
  const missingRecommended = fieldAnalysis.filter(
    (a) => a.status === "missing" && a.field.requirement === "recommended"
  );
  const filledFields = fieldAnalysis.filter((a) => a.status === "filled");

  const completionPercentage = Math.round(
    (filledFields.length / fieldAnalysis.length) * 100
  );

  function handleFieldChange(key: string, value: string) {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }

  function handleTypeChange(typeId: string) {
    const newType = getSourceTypeById(typeId);
    if (newType) {
      setSelectedType(newType);
      setFormData((prev) => ({ ...prev, type: newType.cslType }));
    }
    setShowTypeSelector(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    // Update the type in formData
    const updatedEntry = { ...formData, type: selectedType.cslType };
    onComplete(updatedEntry);
    setSaving(false);
  }

  const TypeIcon = ICON_MAP[selectedType.icon] ?? Globe;

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="size-5 text-primary" />
            {strings.quickEdit}
          </DialogTitle>
          <DialogDescription>{strings.quickEditHint}</DialogDescription>
        </DialogHeader>

        {/* Citation Preview */}
        <div className="rounded-lg border border-border/60 bg-muted/30 p-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
              {strings.resultTitle} ({style.toUpperCase()})
            </span>
            <CompletionBadge percentage={completionPercentage} />
          </div>
          <pre className="wrap-break-word whitespace-pre-wrap font-sans text-foreground text-sm leading-relaxed">
            {citation}
          </pre>
        </div>

        {/* Source Type Selector */}
        <div className="space-y-2">
          <Label className="font-medium text-sm">{strings.sourceType}</Label>
          <button
            className={cn(
              "flex w-full items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-4 py-3 text-left transition-all duration-200",
              "hover:border-primary/50 hover:bg-accent/30",
              "focus:outline-none focus:ring-2 focus:ring-primary/30"
            )}
            onClick={() => setShowTypeSelector(!showTypeSelector)}
            type="button"
          >
            <div className="flex min-w-0 items-center gap-3">
              <TypeIcon className="size-5 text-primary" />
              <div className="min-w-0">
                <div className="font-medium text-foreground">
                  {selectedType.name}
                </div>
                <div className="text-muted-foreground text-xs">
                  {selectedType.description}
                </div>
              </div>
            </div>
            <ChevronDown
              className={cn(
                "size-4 text-muted-foreground transition-transform duration-200",
                showTypeSelector && "rotate-180"
              )}
            />
          </button>
          {showTypeSelector && (
            <div className="grid animate-fade-in gap-2 rounded-lg border border-border/60 bg-muted/20 p-3 sm:grid-cols-3 min-[400px]:grid-cols-2">
              {SOURCE_TYPES.map((type) => {
                const Icon = ICON_MAP[type.icon] ?? Globe;
                const isSelected = selectedType.id === type.id;
                return (
                  <button
                    className={cn(
                      "flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-all duration-200",
                      "hover:bg-accent",
                      isSelected
                        ? "bg-primary/10 font-medium text-primary"
                        : "text-foreground"
                    )}
                    key={type.id}
                    onClick={() => handleTypeChange(type.id)}
                    type="button"
                  >
                    <Icon className="size-4 shrink-0" />
                    {type.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Status Summary */}
        {(missingRequired.length > 0 || missingRecommended.length > 0) && (
          <div className="flex flex-wrap gap-2">
            {missingRequired.length > 0 && (
              <div className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 px-3 py-1 font-medium text-destructive text-xs">
                <AlertCircle className="size-3" />
                {missingRequired.length} required field
                {missingRequired.length !== 1 ? "s" : ""} missing
              </div>
            )}
            {missingRecommended.length > 0 && (
              <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-3 py-1 font-medium text-amber-600 text-xs dark:text-amber-500">
                <Info className="size-3" />
                {missingRecommended.length} recommended field
                {missingRecommended.length !== 1 ? "s" : ""}
              </div>
            )}
          </div>
        )}

        {/* Field Form */}
        <form className="space-y-4" id="wizard-form" onSubmit={handleSubmit}>
          {selectedType.fields.map((field) => (
            <FieldInput
              field={field}
              key={field.key}
              onChange={(value) => handleFieldChange(field.key, value)}
              value={formData[field.key as keyof WizardEntry] ?? ""}
            />
          ))}
        </form>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            onClick={() => onOpenChange(false)}
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button disabled={saving} form="wizard-form" type="submit">
            {saving ? strings.saving : strings.update}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface FieldInputProps {
  field: FieldConfig;
  value: string;
  onChange: (value: string) => void;
}

function FieldStatusBadge({
  isFilled,
  isRequired,
  isRecommended,
}: {
  isFilled: boolean;
  isRequired: boolean;
  isRecommended: boolean;
}) {
  if (isFilled) {
    return (
      <CheckCircle2 className="size-3.5 text-green-600 dark:text-green-500" />
    );
  }
  if (isRequired) {
    return (
      <span className="font-medium text-destructive text-xs">Required</span>
    );
  }
  if (isRecommended) {
    return (
      <span className="text-amber-600 text-xs dark:text-amber-500">
        Recommended
      </span>
    );
  }
  return <span className="text-muted-foreground text-xs">Optional</span>;
}

function FieldInput({ field, value, onChange }: FieldInputProps) {
  const isFilled = value.trim() !== "";
  const isRequired = field.requirement === "required";
  const isRecommended = field.requirement === "recommended";

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Label
          className={cn(
            "font-medium text-sm",
            !isFilled && isRequired && "text-destructive",
            !isFilled && isRecommended && "text-amber-600 dark:text-amber-500"
          )}
          htmlFor={`field-${field.key}`}
        >
          {field.label}
        </Label>
        <FieldStatusBadge
          isFilled={isFilled}
          isRecommended={isRecommended}
          isRequired={isRequired}
        />
      </div>
      <Input
        className={cn(
          "transition-colors",
          !isFilled &&
            isRequired &&
            "border-destructive/50 focus:border-destructive",
          !isFilled &&
            isRecommended &&
            "border-amber-500/50 focus:border-amber-500"
        )}
        id={`field-${field.key}`}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        value={value}
      />
      {field.helpText && (
        <p className="text-muted-foreground text-xs">{field.helpText}</p>
      )}
    </div>
  );
}

function CompletionBadge({ percentage }: { percentage: number }) {
  let color = "bg-destructive/10 text-destructive";
  if (percentage >= 80) {
    color = "bg-green-500/10 text-green-600 dark:text-green-500";
  } else if (percentage >= 50) {
    color = "bg-amber-500/10 text-amber-600 dark:text-amber-500";
  }

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-medium text-xs",
        color
      )}
    >
      {percentage}% complete
    </div>
  );
}
